import React from 'react';
import { motion } from 'framer-motion';
import { Video, Image, FileText, Smartphone } from 'lucide-react';

/**
 * CategoryTabs - Navigation tabs for content type categories
 * 
 * Design matches the Pixar studio aesthetic:
 * - Pill-style tabs similar to StudioNavbar phase switcher
 * - Animated active indicator with layoutId
 * - Consistent indigo/slate color scheme
 */
const CategoryTabs = ({ activeCategory, onCategoryChange, contentCounts = {} }) => {
    const categories = [
        {
            id: 'video',
            label: 'Video',
            icon: Video,
            emoji: ' clips',
            count: contentCounts.video || 8
        },
        {
            id: 'graphics',
            label: 'Graphics',
            icon: Image,
            emoji: ' assets',
            count: contentCounts.graphics || 6
        },
        {
            id: 'text',
            label: 'Text',
            icon: FileText,
            emoji: ' docs',
            count: contentCounts.text || 5
        },
        {
            id: 'social',
            label: 'Social',
            icon: Smartphone,
            emoji: ' posts',
            count: contentCounts.social || 4
        }
    ];

    return (
        <div className="px-6 py-3">
            {/* Tabs Container - Pill Style */}
            <div className="flex items-center bg-white border-2 border-slate-100 shadow-sm rounded-full p-1 gap-1 w-fit">
                {categories.map((category) => {
                    const Icon = category.icon;
                    const isActive = activeCategory === category.id;

                    return (
                        <button
                            key={category.id}
                            onClick={() => onCategoryChange(category.id)}
                            className={`
                                relative px-4 py-2.5 rounded-full text-xs font-bold 
                                transition-all duration-300 flex items-center gap-2
                                ${isActive
                                    ? 'text-indigo-700'
                                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}
                            `}
                        >
                            {/* Active Background Indicator */}
                            {isActive && (
                                <motion.div
                                    layoutId="category-pill"
                                    className="absolute inset-0 bg-indigo-50 border border-indigo-100 rounded-full shadow-sm"
                                    initial={false}
                                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                />
                            )}

                            {/* Icon */}
                            <Icon
                                size={14}
                                className={`relative z-10 ${isActive ? 'text-indigo-600' : 'text-slate-400'}`}
                            />

                            {/* Label */}
                            <span className="relative z-10 hidden sm:inline">{category.label}</span>

                            {/* Count Badge */}
                            <span className={`
                                relative z-10 text-[10px] px-1.5 py-0.5 rounded-full font-bold
                                ${isActive
                                    ? 'bg-indigo-200/50 text-indigo-700'
                                    : 'bg-slate-100 text-slate-500'}
                            `}>
                                {category.count}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default CategoryTabs;